import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import RightTimeApi from "@/API";
import { FaPlusSquare } from "react-icons/fa";
import { PiUserSwitchDuotone } from "react-icons/pi";
import { toast } from "react-toastify";
import HelpEditModal from "@modals/HelpEditModal";
import Enums from "@/enums";
import { decodeHelpLanguage, decodeHelpText, encodeHelpContentForStorage, encodeHelpTitleForStorage, normalizeHelpFormat, readHelpEntryFormat } from "@/utils/helpContentCodec";
import { filterSupportedLanguages } from "@/utils/languageUtils";

type HelpLanguage = {
  id?: number;
  helpContentId?: number;
  languageId?: string | number;
  title?: string;
  content?: string;
};

type HelpEntry = {
  id?: number;
  section?: string;
  identifier?: string;
  companyId?: number | null;
  format?: number | string | null;
  languages?: HelpLanguage[];
};

type SupportedLanguage = {
  id?: number | string;
  name?: string;
  abbrev?: string;
};

const HELP_ROW_HEIGHTS_STORAGE_KEY = "helpTabRowHeights";

export default function AdminHelpTab(props: {
  tabData: { activeTab?: string | null; updateSpinner?: (show: boolean) => void };
  embedded?: boolean;
  defaultSectionFilter?: string;
  onContentChanged?: () => void;
}) {
  const { activeTab, updateSpinner } = props.tabData;
  const { embedded = false, defaultSectionFilter, onContentChanged } = props;
  const [helpItems, setHelpItems] = useState<HelpEntry[]>([]);
  const [supportedLanguages, setSupportedLanguages] = useState<SupportedLanguage[]>([]);
  const [selectedHelpItem, setSelectedHelpItem] = useState<HelpEntry | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isTranslateMode, setIsTranslateMode] = useState(false);
  const [translateDrafts, setTranslateDrafts] = useState<Record<string, Record<string, { id?: number; helpContentId?: number; title: string; content: string }>>>({});
  const [savingRows, setSavingRows] = useState<Record<string, boolean>>({});
  const [rowHeights, setRowHeights] = useState<Record<string, number>>(() => {
    try {
      const stored = localStorage.getItem(HELP_ROW_HEIGHTS_STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });
  const [resizingRowKey, setResizingRowKey] = useState<string | null>(null);
  const resizeStateRef = useRef<{ rowKey: string; startY: number; startHeight: number } | null>(null);
  const translateRowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});

  const sortedHelpItems = useMemo(() => {
    return [...helpItems].sort((a, b) => {
      const aSection = (a.section || "").toLowerCase();
      const bSection = (b.section || "").toLowerCase();
      if (aSection !== bSection) return aSection.localeCompare(bSection);
      return (a.identifier || "").toLowerCase().localeCompare((b.identifier || "").toLowerCase());
    });
  }, [helpItems]);

  const languageAbbrevById = useMemo(() => {
    const map: Record<string, string> = {};
    supportedLanguages.forEach((language) => {
      map[String(language.id)] = language.abbrev || language.name || String(language.id);
    });
    return map;
  }, [supportedLanguages]);

  function entryKey(entry: HelpEntry) {
    return String(entry.id ?? `${entry.section || ""}|${entry.identifier || ""}|${entry.companyId ?? ""}`);
  }

  function getLanguageFromEntry(entry: HelpEntry, languageId: number | string) {
    const langs = Array.isArray(entry.languages) ? entry.languages : [];
    const normalizedId = Number(languageId);
    return langs.find((lang) => {
      const decoded = decodeHelpLanguage(lang);
      return Number(decoded.languageId || 0) === normalizedId;
    }) || null;
  }

  function buildDrafts(items: HelpEntry[], langs: SupportedLanguage[]) {
    const next: Record<string, Record<string, { id?: number; helpContentId?: number; title: string; content: string }>> = {};
    items.forEach((entry) => {
      const key = entryKey(entry);
      next[key] = {};
      langs.forEach((language) => {
        const found = getLanguageFromEntry(entry, language.id);
        const decoded = decodeHelpLanguage(found);
        next[key][String(language.id)] = {
          id: decoded?.id,
          helpContentId: decoded?.helpContentId,
          title: decoded?.title ?? "",
          content: decoded?.content ?? "",
        };
      });
    });
    return next;
  }

  useEffect(() => {
    if (!isTranslateMode) return;
    setTranslateDrafts(buildDrafts(helpItems, supportedLanguages));
  }, [isTranslateMode, helpItems, supportedLanguages]);

  function formatEntryLanguages(entry: HelpEntry) {
    const langs = Array.isArray(entry.languages) ? entry.languages : [];
    if (langs.length === 0) return "";

    const uniqueAbbrevs: string[] = [];
    langs.forEach((lang) => {
      const langKey = String(lang.languageId ?? "").trim();
      if (!langKey) return;
      const abbrev = languageAbbrevById[langKey] || langKey;
      if (!uniqueAbbrevs.includes(abbrev)) {
        uniqueAbbrevs.push(abbrev);
      }
    });

    return uniqueAbbrevs.join(", ");
  }

  useEffect(() => {
    if (!embedded && !isOnThisTab()) return;
    void loadDataAsync();
  }, [activeTab, embedded]);

  function isOnThisTab() {
    return embedded || activeTab === "help";
  }

  async function loadDataAsync() {
    if (typeof updateSpinner === "function") updateSpinner(true);
    try {
      const [helpResp, languagesResp] = await Promise.all([
        RightTimeApi.getAsync("/help/all"),
        RightTimeApi.getAsync("/Languages"),
      ]);

      if (helpResp?.ok && Array.isArray(helpResp.jsonData)) {
        setHelpItems(helpResp.jsonData);
      } else {
        setHelpItems([]);
      }

      if (languagesResp?.ok && Array.isArray(languagesResp.jsonData)) {
        setSupportedLanguages(filterSupportedLanguages(languagesResp.jsonData));
      } else {
        setSupportedLanguages([]);
      }
    } finally {
      if (typeof updateSpinner === "function") updateSpinner(false);
    }
  }

  async function handleHelpSavedAsync() {
    await loadDataAsync();
    if (typeof onContentChanged === "function") {
      onContentChanged();
    }
  }

  function openEditor(entry: HelpEntry) {
    const key = entryKey(entry);
    const freshEntry = helpItems.find((item) => entryKey(item) === key) ?? entry;
    setSelectedHelpItem(freshEntry);
    setIsEditModalOpen(true);
  }

  function handleAddClick() {
    setSelectedHelpItem({
      id: 0,
      section: defaultSectionFilter || "",
      identifier: "",
      format: defaultSectionFilter ? Enums.CONTENT_FORMAT.HTML : Enums.CONTENT_FORMAT.Text,
      languages: [],
    });
    setIsEditModalOpen(true);
  }

  function getDraftCell(entry: HelpEntry, languageId: number | string | undefined, field: "title" | "content") {
    const key = entryKey(entry);
    const row = translateDrafts[key] || {};
    return row[String(languageId)]?.[field] || "";
  }

  function handleRowResizeStart(rowKey: string, event: React.MouseEvent<HTMLDivElement>) {
    event.preventDefault();
    event.stopPropagation();

    const tr = event.currentTarget.closest("tr");
    const fallbackHeight = tr?.getBoundingClientRect().height ?? 120;
    const startHeight = rowHeights[rowKey] ?? fallbackHeight;

    resizeStateRef.current = {
      rowKey,
      startY: event.clientY,
      startHeight,
    };

    setResizingRowKey(rowKey);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "row-resize";
  }

  function resetRowHeight(rowKey: string) {
    setRowHeights((prev) => {
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
  }

  function resetAllRowHeights() {
    setRowHeights({});
  }

  // Persist row heights to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(HELP_ROW_HEIGHTS_STORAGE_KEY, JSON.stringify(rowHeights));
    } catch (error) {
      console.warn("Failed to persist help tab row heights:", error);
    }
  }, [rowHeights]);

  useEffect(() => {
    const onMouseMove = (event: MouseEvent) => {
      const state = resizeStateRef.current;
      if (!state) return;

      const delta = event.clientY - state.startY;
      const nextHeight = Math.max(72, Math.round(state.startHeight + delta));
      setRowHeights((prev) => {
        if (prev[state.rowKey] === nextHeight) return prev;
        return {
          ...prev,
          [state.rowKey]: nextHeight,
        };
      });
    };

    const onMouseUp = () => {
      if (!resizeStateRef.current) return;
      setResizingRowKey(null);
      resizeStateRef.current = null;
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };

    if (resizeStateRef.current) {
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
  }, []);

  useEffect(() => {
    Object.entries(translateRowRefs.current).forEach(([key, rowEl]) => {
      if (!rowEl) return;
      const height = rowHeights[key];
      rowEl.style.height = height ? `${height}px` : "";
    });
  }, [rowHeights, isTranslateMode, sortedHelpItems.length]);

  function updateDraftCell(entry: HelpEntry, languageId: number | string | undefined, field: "title" | "content", value: string) {
    const key = entryKey(entry);
    setTranslateDrafts((prev) => {
      const row = prev[key] || {};
      const existing = row[String(languageId)] || { title: "", content: "" };
      return {
        ...prev,
        [key]: {
          ...row,
          [String(languageId)]: {
            ...existing,
            [field]: value,
          },
        },
      };
    });
  }

  function isRowDirty(entry: HelpEntry) {
    const key = entryKey(entry);
    const row = translateDrafts[key] || {};
    return supportedLanguages.some((language) => {
      const langIdKey = String(language.id);
      const draft = row[langIdKey] || { title: "", content: "" };
      const original = getLanguageFromEntry(entry, language.id);
      const originalTitle = decodeHelpText(original?.title || "");
      const originalContent = decodeHelpText(original?.content || "");
      return draft.title !== originalTitle || draft.content !== originalContent;
    });
  }

  async function saveTranslateRowAsync(entry: HelpEntry) {
    const key = entryKey(entry);
    if (savingRows[key]) return;
    if (!isRowDirty(entry)) return;

    const row = translateDrafts[key] || {};
    const entryFormat = readHelpEntryFormat(entry);
    const payloadLanguages = supportedLanguages
      .map((language) => {
        const langIdKey = String(language.id);
        const draft = row[langIdKey] || { title: "", content: "" };
        const title = (draft.title || "").trim();
        const content = (draft.content || "").trim();
        return {
          id: draft.id,
          helpContentId: draft.helpContentId,
          languageId: language.id,
          title: encodeHelpTitleForStorage(title),
          content: encodeHelpContentForStorage(content, entryFormat),
          hasAny: !!(title || content),
        };
      })
      .filter((lang) => lang.hasAny)
      .map(({ hasAny, ...lang }) => lang);

    const payload = {
      id: entry.id ?? 0,
      section: entry.section || "",
      identifier: entry.identifier || "",
      companyId: entry.companyId ?? null,
      format: entryFormat,
      languages: payloadLanguages,
    };

    setSavingRows((prev) => ({ ...prev, [key]: true }));
    try {
      const resp = await RightTimeApi.sendAsync("/Help", payload);
      if (!resp?.ok) {
        toast.error(`Failed to save translations for ${entry.identifier || "row"}.`);
        return;
      }

      if (resp.jsonData) {
        const updated = resp.jsonData as HelpEntry;
        setHelpItems((prev) => prev.map((item) => (entryKey(item) === key ? updated : item)));
      }
    } catch {
      toast.error(`Failed to save translations for ${entry.identifier || "row"}.`);
    } finally {
      setSavingRows((prev) => ({ ...prev, [key]: false }));
    }
  }

  if (!isOnThisTab()) return null;

  return (
    <>
      <div className={`tableWrapper dimensionTable${isTranslateMode ? " helpTranslateMode" : ""}`}>
        <table>
          <caption>
            <span className="title">Help Content</span>
            <span
              className="actionIcon plusIcon floatRight contentAddAction"
              onClick={handleAddClick}
              title="Add Help Content"
            >
              <FaPlusSquare />
            </span>
            {isTranslateMode && (
              <span
                className="actionIcon resetIcon floatRight"
                onClick={resetAllRowHeights}
                title="Reset all row heights to default"
                style={{ marginRight: "8px", cursor: "pointer", opacity: 0.7 }}
              >
                ↺
              </span>
            )}
            <span
              className={`actionIcon plusIcon floatRight contentAddAction ${isTranslateMode ? "active" : ""}`}
              onClick={() => setIsTranslateMode((prev) => !prev)}
              title={isTranslateMode ? "Exit Translate Mode" : "Enter Translate Mode"}
            >
              <PiUserSwitchDuotone />
            </span>
          </caption>
          <thead>
            {!isTranslateMode ? (
              <tr>
                <th>Id</th>
                <th>Section</th>
                <th>Identifier</th>
                <th>Languages</th>
              </tr>
            ) : (
              <tr>
                <th className="translateIdentifierCol">Identifier</th>
                {supportedLanguages.map((language) => (
                  <Fragment key={`lang-cols-${language.id}`}>
                    <th className="translateTitleCol">{`${language.abbrev || language.name || language.id} Title`}</th>
                    <th className="translateContentCol">{`${language.abbrev || language.name || language.id} Content`}</th>
                  </Fragment>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {!isTranslateMode
              ? sortedHelpItems.map((entry) => (
                  <tr key={`${entry.id}-${entry.section}-${entry.identifier}`} className="clickableRow" onClick={() => openEditor(entry)}>
                    <td>{entry.id}</td>
                    <td><span style={{ fontStyle: entry.section ? 'normal' : 'italic', opacity: entry.section ? 1 : 0.7 }}>{entry.section ? entry.section : '(Global)'}</span></td>
                    <td>{entry.identifier}</td>
                    <td>{formatEntryLanguages(entry)}</td>
                  </tr>
                ))
              : sortedHelpItems.map((entry) => {
                  const key = entryKey(entry);
                  return (
                    <tr
                      key={`${key}-translate`}
                      ref={(el) => {
                        translateRowRefs.current[key] = el;
                      }}
                    >
                      <td className="translateIdentifierCol">
                        {entry.identifier}
                        <div
                          className={`translateRowResizeHandle ${resizingRowKey === key ? "active" : ""}`}
                          onMouseDown={(e) => handleRowResizeStart(key, e)}
                          title="Drag to resize row"
                        />
                      </td>
                      {supportedLanguages.map((language) => (
                        <Fragment key={`${key}-${language.id}-cells`}>
                          <td className="translateTitleCol">
                            <textarea
                              rows={2}
                              value={getDraftCell(entry, language.id, "title")}
                              onChange={(e) => updateDraftCell(entry, language.id, "title", e.target.value)}
                              onBlur={() => saveTranslateRowAsync(entry)}
                              disabled={!!savingRows[key]}
                              title={`${language.name || language.abbrev || language.id} title`}
                            />
                          </td>
                          <td className="translateContentCol">
                            <textarea
                              rows={3}
                              value={getDraftCell(entry, language.id, "content")}
                              onChange={(e) => updateDraftCell(entry, language.id, "content", e.target.value)}
                              onBlur={() => saveTranslateRowAsync(entry)}
                              disabled={!!savingRows[key]}
                              title={`${language.name || language.abbrev || language.id} content`}
                            />
                          </td>
                        </Fragment>
                      ))}
                    </tr>
                  );
                })}
          </tbody>
        </table>
      </div>

      <HelpEditModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedHelpItem(null);
        }}
        helpEntry={selectedHelpItem}
        supportedLanguages={supportedLanguages}
        initialLanguageId={1}
        onSaved={handleHelpSavedAsync}
      />
    </>
  );
}
